import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Code2, Smartphone, Palette, Layers3, Search, ShoppingBag, Zap, Globe, ArrowRight } from "lucide-react";
import { SERVICES, SERVICE_LINK } from "@/constants/testIds";

const services = [
  {
    key: "web-development",
    icon: Code2,
    title: "Web Development",
    tag: "S/01",
    desc: "Custom web platforms engineered for scale — from marketing sites to enterprise dashboards. Clean architecture, tuned performance.",
    stack: ["React", "Next.js", "Node", "Laravel"],
  },
  {
    key: "mobile-application",
    icon: Smartphone,
    title: "Mobile Application",
    tag: "S/02",
    desc: "Native-feel apps that ship. iOS, Android, and cross-platform via Flutter/React Native — with offline-ready sync layers.",
    stack: ["React Native", "Flutter", "Swift", "Kotlin"],
  },
  {
    key: "ui-ux-design",
    icon: Palette,
    title: "UI / UX Design",
    tag: "S/03",
    desc: "Interfaces designed like game HUDs — every pixel intentional. Research, wireframes, and clickable prototypes.",
    stack: ["Figma", "Framer", "Design Systems"],
  },
  {
    key: "cms",
    icon: Layers3,
    title: "CMS Systems",
    tag: "S/04",
    desc: "Headless or WordPress-native. Editorial workflows for teams of 2 or 2,000, without slowing your site to a crawl.",
    stack: ["WordPress", "Strapi", "Sanity"],
  },
  {
    key: "seo",
    icon: Search,
    title: "SEO",
    tag: "S/05",
    desc: "Technical SEO, structured data, and content strategy. Rank for what actually converts — not vanity keywords.",
    stack: ["GSC", "GA4", "Schema"],
  },
  {
    key: "ecommerce",
    icon: ShoppingBag,
    title: "e-Commerce",
    tag: "S/06",
    desc: "Storefronts, checkouts, and inventory pipelines tuned for conversion. Custom builds or Shopify/Woo when it fits.",
    stack: ["Shopify", "WooCommerce", "Custom"],
  },
  {
    key: "pwa",
    icon: Zap,
    title: "Progressive Web App",
    tag: "S/07",
    desc: "Installable, offline-capable web experiences with app-store-quality UX and none of the review queues.",
    stack: ["PWA", "Service Workers", "IndexedDB"],
  },
];

const gisService = {
  key: "gis",
  icon: Globe,
  title: "GIS Systems",
  tag: "S/08",
  desc: "Location-aware platforms — live mapping, field capture, spatial analytics, and satellite pipelines for environmental, gov, and enterprise ops.",
  stack: ["Mapbox", "Leaflet", "PostGIS", "Turf.js", "Field-mobile"],
};

export default function Services() {
  return (
    <section
      id="services"
      data-testid={SERVICES.section}
      className="relative py-24 md:py-32 border-t border-cyan-900/40"
    >
      <div className="absolute inset-0 hud-grid opacity-30 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-8 relative">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <div className="overline">/ 01 services.matrix</div>
            <h2 className="mt-4 font-mono-display text-3xl sm:text-4xl md:text-5xl font-extrabold uppercase tracking-tighter text-slate-100">
              What we <span className="text-neon-cyan">build</span>
            </h2>
          </div>
          <p className="max-w-md text-slate-400 font-body">
            Eight disciplines. One studio. Pick your class — or run a full-party engagement.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.article
                key={s.key}
                data-testid={SERVICES.card(s.key)}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.06 }}
                className="group relative p-6 md:p-7 bg-[#0a0a0a] border border-white/5 hover:border-cyan-500/40 transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_0_28px_rgba(34,211,238,0.16)] hud-corners"
              >
                <span className="hud-c-tl" />
                <span className="hud-c-br" />

                <div className="flex items-start justify-between">
                  <span className="w-11 h-11 flex items-center justify-center border border-cyan-500/40 text-cyan-400 group-hover:bg-cyan-500/10 transition-colors duration-300">
                    <Icon size={20} />
                  </span>
                  <span className="font-hud text-cyan-400/70">{s.tag}</span>
                </div>

                <h3 className="mt-6 font-mono-display text-xl uppercase tracking-tight text-slate-100">
                  {s.title}
                </h3>
                <p className="mt-3 text-slate-400 font-body text-sm leading-relaxed">
                  {s.desc}
                </p>

                <div className="mt-5 flex flex-wrap gap-2">
                  {s.stack.map((tech) => (
                    <span key={tech} className="tag-pill">{tech}</span>
                  ))}
                </div>
              </motion.article>
            );
          })}

          {/* GIS — featured card spanning 2 cols on lg */}
          <motion.article
            data-testid={SERVICES.card(gisService.key)}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.55 }}
            className="group relative overflow-hidden p-6 md:p-8 bg-[#0a0a0a] border border-cyan-500/40 hover:border-cyan-400 transition-[border-color,box-shadow] duration-300 hover:shadow-[0_0_38px_rgba(34,211,238,0.22)] sm:col-span-2 hud-corners"
            style={{
              backgroundImage:
                "radial-gradient(circle at 15% 25%, rgba(34,211,238,0.16), transparent 42%), radial-gradient(circle at 85% 75%, rgba(217,70,239,0.10), transparent 45%)",
            }}
          >
            <span className="hud-c-tl" />
            <span className="hud-c-br" />

            {/* Orbit rings decoration */}
            <div className="hidden md:block absolute -right-16 -bottom-16 w-64 h-64 pointer-events-none opacity-60">
              <div className="absolute inset-0 rounded-full border border-cyan-500/25 animate-[spin_24s_linear_infinite]" />
              <div className="absolute inset-6 rounded-full border border-fuchsia-500/20 animate-[spin_18s_linear_infinite_reverse]" />
              <div className="absolute inset-12 rounded-full border border-cyan-500/40" />
              <div className="absolute inset-0 flex items-center justify-center">
                <Globe className="w-24 h-24 text-cyan-400/70 animate-[spin_28s_linear_infinite]" strokeWidth={1} />
              </div>
            </div>

            <div className="relative flex items-start justify-between">
              <span className="w-12 h-12 flex items-center justify-center border border-cyan-500/60 text-cyan-400 bg-cyan-500/10">
                <gisService.icon size={22} />
              </span>
              <div className="flex items-center gap-2">
                <span className="tag-pill" style={{ borderColor: "rgba(217,70,239,0.5)", color: "#d946ef", background: "rgba(217,70,239,0.06)" }}>
                  Featured · New
                </span>
                <span className="font-hud text-cyan-400/70">{gisService.tag}</span>
              </div>
            </div>

            <h3 className="relative mt-6 font-mono-display text-2xl md:text-3xl uppercase tracking-tight text-slate-100">
              {gisService.title}
            </h3>
            <p className="relative mt-3 max-w-2xl text-slate-300 font-body text-sm md:text-base leading-relaxed">
              {gisService.desc}
            </p>

            <div className="relative mt-5 flex flex-wrap gap-2">
              {gisService.stack.map((tech) => (
                <span key={tech} className="tag-pill">{tech}</span>
              ))}
            </div>

            <div className="relative mt-7 flex flex-wrap gap-3">
              <Link
                to="/gis"
                data-testid={SERVICE_LINK.gisPage}
                className="btn-neon"
              >
                Explore_GIS_page <ArrowRight size={13} />
              </Link>
              <a
                href="#portfolio"
                data-testid={SERVICE_LINK.gisPortfolio}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("portfolio")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="btn-ghost"
              >
                See_completed_projects
              </a>
            </div>
          </motion.article>
        </div>
      </div>
    </section>
  );
}
